import { tokens } from '@neo4j-ndl/base';
import { Button, IconButton, TextLink, Typography } from '@neo4j-ndl/react';
import { ArrowPathIconOutline, ChevronRightIconOutline } from '@neo4j-ndl/react/icons';
import { type FleetManagementTypes, type MetricsResult, useOpsContext } from '@nx/state';
import { isNonEmptyString, isNotNullish, isNullish } from '@nx/stdlib';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { MAX_DATABASES_FOR_METRICS } from '../fleet-management/shared/constants';
import { ApiErrorBanner } from '../shared/components';
import { fakeMetrics } from '../shared/utils';
import { DeploymentDashboardCharts } from './charts/deployments/deployment-dashboard-charts';
import { DashboardLineCard } from './charts/line-cards';
import type { ChartContext } from './charts/types';
import { chartKey, useMetricsQuery, useMetricsViewState } from './hooks';
import { FleetDbmsMetricSource } from './shared/fleet-dbms-metric-source';

interface DeploymentDashboardMetricsProps {
  projectId: string;
  chartContext: ChartContext;
  deployment: FleetManagementTypes.DeploymentDetails;
}

const DeploymentDashboardMetrics = ({ projectId, chartContext, deployment }: DeploymentDashboardMetricsProps) => {
  const metricSource = useMemo(() => new FleetDbmsMetricSource(projectId, deployment, false), [projectId, deployment]);

  const metricsResult = useMetricsQuery({
    metricSource,
    timeRange: chartContext.timeRange,
    chartProps: DeploymentDashboardCharts,
    chartType: 'DASHBOARD',
  });

  if (metricsResult.error) {
    return <ApiErrorBanner error={metricsResult.error} />;
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {DeploymentDashboardCharts.map((chart) => (
        <DashboardLineCard
          key={chartKey(chart)}
          multiChartProps={chart}
          context={chartContext}
          metricsResult={metricsResult}
        />
      ))}
    </div>
  );
};

const FakeDeploymentDashboardMetrics = ({
  deployment,
  chartContext,
  overlayText,
  onTextClick,
}: DeploymentDashboardMetricsProps & {
  overlayText?: string;
  onTextClick?: () => void;
}) => {
  const fakeMetricsResult: MetricsResult = useMemo(
    () => ({
      metrics: fakeMetrics(deployment.name, '24H', DeploymentDashboardCharts, 40),
      isLoading: false,
    }),
    [deployment],
  );

  return (
    <div className="relative">
      <div
        className="pointer-events-none grid grid-cols-1 gap-4 opacity-40 blur-[2px] md:grid-cols-2 xl:grid-cols-3"
        aria-disabled={true}
      >
        {DeploymentDashboardCharts.map((chart) => (
          <DashboardLineCard
            key={chartKey(chart)}
            multiChartProps={chart}
            context={chartContext}
            metricsResult={fakeMetricsResult}
          />
        ))}
      </div>
      {isNonEmptyString(overlayText) && (
        <div className="absolute left-[50%] top-[50%] -translate-x-1/2 -translate-y-1/2">
          {onTextClick && (
            <TextLink
              className="cursor-pointer"
              href="#"
              htmlAttributes={{
                onClick: onTextClick,
                tabIndex: 0,
              }}
            >
              {overlayText}
            </TextLink>
          )}
          {!onTextClick && <Typography variant="body-large">{overlayText}</Typography>}
        </div>
      )}
    </div>
  );
};

export const DeploymentResources = ({
  deployment,
  projectId,
}: {
  deployment: FleetManagementTypes.DeploymentDetails;
  projectId: string;
}) => {
  const [showResources, setShowResources] = useState(false);
  const navigate = useNavigate();
  const { setSelectedInstance } = useOpsContext();

  const { chartContext, refreshTimeRange } = useMetricsViewState({ timePeriod: '24H' });

  const handleToggleResource = () => {
    setShowResources((show) => !show);
  };

  const handleViewAllMetrics = () => {
    if (isNotNullish(deployment.dbms)) {
      setSelectedInstance(deployment.deploymentId);
      void navigate(`/projects/${projectId}/metrics`);
    }
  };

  const isCommunityEdition = useMemo(() => {
    return isNotNullish(deployment.dbms) && deployment.dbms.edition === 'community';
  }, [deployment]);

  const metricsDisabledReason = useMemo(() => {
    if (isNullish(deployment.dbms)) {
      return 'Connect your self-managed deployment to view metrics.';
    }
    if (isCommunityEdition) {
      return 'Community edition deployments do not support metrics. Upgrade to AuraDB to access metrics.';
    }
    if (deployment.dbms.databases.length > MAX_DATABASES_FOR_METRICS) {
      return `Metric collection is disabled for deployments with more than ${MAX_DATABASES_FOR_METRICS} databases.`;
    }
    return undefined;
  }, [deployment.dbms, isCommunityEdition]);

  return (
    <div aria-label={`Deployment metrics for ${deployment.name}`} className="relative flex flex-col gap-4 p-4">
      <div className="flex w-full justify-between">
        <div
          role="button"
          tabIndex={0}
          aria-label="Toggle Metrics"
          aria-pressed={showResources}
          onKeyDown={(e) => e.key === 'Enter' && handleToggleResource()}
          className="text-palette-neutral-text-weak flex cursor-pointer items-center gap-2"
          onClick={handleToggleResource}
        >
          <ChevronRightIconOutline
            aria-label="Show Metrics"
            className="size-4"
            style={{
              transform: showResources ? 'rotate(90deg)' : 'rotate(0deg)',
              transitionDuration: tokens.transitions.values.duration.quick,
            }}
          />
          {/* add margin right to balance tab outline */}
          <Typography variant="subheading-small" className="mr-2">
            Metrics
          </Typography>
        </div>

        {isNullish(metricsDisabledReason) && (
          <div className="flex items-center gap-2">
            {showResources && (
              <IconButton
                ariaLabel="Click to refresh charts"
                size="small"
                onClick={refreshTimeRange}
                isClean={true}
                htmlAttributes={{
                  title: 'Click to refresh charts',
                }}
              >
                <ArrowPathIconOutline />
              </IconButton>
            )}
            <Button fill="outlined" size="small" color="neutral" onClick={handleViewAllMetrics}>
              View all metrics
            </Button>
          </div>
        )}
      </div>
      {showResources &&
        (isNullish(metricsDisabledReason) ? (
          <DeploymentDashboardMetrics deployment={deployment} projectId={projectId} chartContext={chartContext} />
        ) : (
          <FakeDeploymentDashboardMetrics
            deployment={deployment}
            chartContext={chartContext}
            overlayText={metricsDisabledReason}
            projectId={projectId}
            onTextClick={
              isCommunityEdition
                ? () => {
                    window.open('https://neo4j.com/docs/aura/tutorials/migration/', '_blank', 'noopener,noreferrer');
                  }
                : undefined
            }
          />
        ))}
    </div>
  );
};
